import { signOutSafely } from '../utils/push-notifications';
import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User } from 'firebase/auth';
import { FileText, Heart, LogOut, Menu, Settings, Users, X } from 'lucide-react';

import { BrandLogo } from './BrandLogo';
import '../dashboard.css';

interface AppShellProps {
    user: User;
    children: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({ user, children }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const planBadge = (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
            {t('appShell.plan.earlyAccess', 'Free')}
        </span>
    );

    const navItems = [
        { to: '/dashboard', label: t('appShell.nav.parents', 'Parents'), icon: Users },
        { to: '/reports', label: t('appShell.nav.reports', 'Reports'), icon: FileText },
        { to: '/settings', label: t('appShell.nav.settings', 'Settings'), icon: Settings },
    ];

    const handleSignOut = async () => {
        try {
            await signOutSafely();
            navigate('/');
        } catch {
            window.alert(t('app.settings.signOutError', 'Could not revoke device notifications. Please reconnect and try again.'));
        }
    };

    const linkClasses = ({ isActive }: { isActive: boolean }, to: string) =>
        `app-nav-link ${isActive || (to === '/dashboard' && pathname.startsWith('/parent/')) ? 'is-active' : ''}`;

    return (
        <div className="app-shell">
            <a href="#app-content" className="app-skip">{t('appShell.skip', 'Skip to content')}</a>
            <header className="app-header">
                <div className="app-header-inner">
                    <BrandLogo to="/dashboard" />
                    <nav className="app-desktop-nav" aria-label={t('appShell.navigation', 'Main navigation')}>
                        {navItems.map((item) => (
                            <NavLink key={item.to} to={item.to} className={(state) => linkClasses(state, item.to)}>
                                <item.icon className="w-4 h-4" />
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="app-account">
                        {planBadge}
                        <NavLink to="/settings" className="app-account-avatar" title={user.email ?? undefined}
                            aria-label={t('appShell.account', 'Account settings')}>
                            {(user.displayName || user.email || '?').slice(0, 1).toUpperCase()}
                        </NavLink>
                        <button onClick={handleSignOut} className="app-signout"
                            aria-label={t('appShell.signOut', 'Sign Out')} title={t('appShell.signOut', 'Sign Out')}>
                            <LogOut className="w-4 h-4" />
                        </button>
                        <button onClick={() => setIsMobileMenuOpen((open) => !open)} className="app-menu-toggle"
                            aria-expanded={isMobileMenuOpen} aria-controls="app-mobile-menu"
                            aria-label={isMobileMenuOpen ? t('appShell.closeMenu', 'Close menu') : t('appShell.openMenu', 'Open menu')}>
                            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
                {isMobileMenuOpen && (
                    <nav id="app-mobile-menu" className="app-mobile-nav" aria-label={t('appShell.navigation', 'Main navigation')}>
                        {navItems.map((item) => (
                            <NavLink key={item.to} to={item.to} onClick={() => setIsMobileMenuOpen(false)}
                                className={(state) => linkClasses(state, item.to)}>
                                <item.icon className="w-4 h-4" />{item.label}
                            </NavLink>
                        ))}
                        <button onClick={handleSignOut} className="app-nav-link">
                            <LogOut className="w-4 h-4" />{t('appShell.signOut', 'Sign Out')}
                        </button>
                    </nav>
                )}
            </header>
            <main id="app-content" tabIndex={-1} className="app-content">{children}</main>
            <footer className="app-footer">
                <Heart className="w-3.5 h-3.5" />
                <span>{t('appShell.footer', 'A little connection. A little peace of mind.')}</span>
            </footer>
        </div>
    );
};
